import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getFallbackUnitPrice = (item) => {
  return (
    toNumber(item?.unit_price) ||
    toNumber(item?.promotional_price) ||
    toNumber(item?.selling_price) ||
    toNumber(item?.price) ||
    0
  );
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem("cart");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartItems));
  }, [cartItems]);

  /**
   * Single-product cart (replace cart) + supports pricing snapshot
   * pricing = { unitPrice, discountRate, discountLabel }
   */
  const addToCart = (product, quantity = 1, pricing = null) => {
    if (!product) return;

    const q = Math.max(1, toNumber(quantity) || 1);

    const unitFromPricing = toNumber(pricing?.unitPrice);
    const discountRate = toNumber(pricing?.discountRate);
    const discountLabel = (pricing?.discountLabel || "").toString();

    setCartItems([
      {
        ...product,
        quantity: q,

        // Pricing snapshot to keep checkout totals consistent
        unit_price: unitFromPricing > 0 ? unitFromPricing : null,
        discount_rate: discountRate > 0 ? discountRate : 0,
        discount_label: discountLabel,
      },
    ]);
  };

  // Remove (single product)
  const removeFromCart = () => setCartItems([]);

  /**
   * Update quantity and recompute discount pricing if possible.
   * If you pass pricingCalculator, we will recalc; otherwise we keep stored unit_price.
   *
   * In your app, the easiest is: Checkout page calls updateQuantity(productId, newQty)
   * and CartContext recalculates discounts based on the product's base unit price.
   */
  const updateQuantity = (productId, quantity) => {
    const q = toNumber(quantity);

    if (q < 1) {
      setCartItems([]);
      return;
    }

    setCartItems((prev) => {
      if (!prev.length) return [];

      const item = prev[0];
      if (item.id !== productId) {
        // single item cart; if mismatch, keep as is
        return prev;
      }

      // Recompute discount based on your rules:
      // 1 piece: 0%, 2 pieces: 8%, 3+ pieces: 12%
      const discountRate = q >= 3 ? 0.12 : q >= 2 ? 0.08 : 0;

      // Base unit is promo if exists else selling/price
      const baseUnit =
        toNumber(item.promotional_price) > 0
          ? toNumber(item.promotional_price)
          : toNumber(item.selling_price) > 0
            ? toNumber(item.selling_price)
            : toNumber(item.price);

      const unitPrice = baseUnit > 0 ? baseUnit * (1 - discountRate) : getFallbackUnitPrice(item);

      const discountLabel = q >= 3 ? "3 pièces (-12%)" : q >= 2 ? "2 pièces (-8%)" : "1 pièce";

      return [
        {
          ...item,
          quantity: q,
          unit_price: unitPrice,
          discount_rate: discountRate,
          discount_label: discountLabel,
        },
      ];
    });
  };

  const clearCart = () => setCartItems([]);

  // Helpers (single item)
  const cartItem = cartItems?.[0] || null;

  const getCartTotal = () => {
    if (!cartItem) return 0;
    const unit = getFallbackUnitPrice(cartItem);
    return unit * (toNumber(cartItem.quantity) || 0);
  };

  const getCartCount = () => {
    if (!cartItem) return 0;
    return toNumber(cartItem.quantity) || 0;
  };

  const getCartUnitPrice = () => {
    if (!cartItem) return 0;
    return getFallbackUnitPrice(cartItem);
  };

  const value = useMemo(
    () => ({
      cartItems,
      cartItem, // convenience
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartCount,
      getCartUnitPrice,
    }),
    [cartItems]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
